import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card } from '../molecules/Card';
import EmptyState from '../molecules/EmptyState';
import Pagination from '../molecules/Pagination';
import SortableTh from '../atoms/SortableTh';

/**
 * DataTable — standard list table (Card + table shell + states + pagination).
 *
 * Markup follows AGENTS.md §4:
 *   .table-container.table-responsive > table.table.[module]-table
 *
 * columns: [{
 *   key,                 // unique id
 *   label,               // header text (also used as mobile `data-label`)
 *   header,              // optional node rendered in the <th> instead of `label` (e.g. select-all checkbox)
 *   sortKey,             // makes the header sortable
 *   align: 'right',      // right-aligned numeric column (`num`)
 *   main: true,          // first/title cell (`td-main`, gets the mobile expand toggle)
 *   className,           // extra class for th + td
 *   render: (row, index) => node
 * }]
 *
 * States: pass `loading`, `error` (+ `onRetry`), and `empty` (EmptyState props).
 * `actions(row)` renders the trailing "Aksi" column (`[module]-table__actions`).
 */
export default function DataTable({
  module = 'data',
  columns,
  rows,
  rowKey,
  rowClassName,
  onRowClick,
  actions,
  actionsLabel = 'Aksi',
  sort,
  loading = false,
  loadingTitle = 'Memuat data…',
  error,
  errorTitle = 'Gagal memuat data',
  onRetry,
  empty = {},
  pagination,
  bare = false,
  className = '',
}) {
  const [expandedKey, setExpandedKey] = useState(null);
  const colSpan = columns.length + (actions ? 1 : 0);
  const actionsClass = `${module}-table__actions`;

  const stateRow = (content) => (
    <tr>
      <td colSpan={colSpan}>{content}</td>
    </tr>
  );

  let body;
  if (loading) {
    body = stateRow(<EmptyState variant="loading" title={loadingTitle} />);
  } else if (error) {
    body = stateRow(
      <EmptyState
        variant="error"
        title={errorTitle}
        hint={error.message ?? String(error)}
        action={onRetry ? { label: 'Coba Lagi', onClick: onRetry } : undefined}
      />
    );
  } else if (rows.length === 0) {
    body = stateRow(<EmptyState title="Tidak ada data" {...empty} />);
  } else {
    body = rows.map((row, index) => {
      const key = rowKey(row);
      const expanded = expandedKey === key;
      return (
        <tr
          key={key}
          className={[expanded ? 'is-expanded' : 'is-collapsed', rowClassName?.(row)]
            .filter(Boolean)
            .join(' ')}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
        >
          {columns.map((col) =>
            col.main ? (
              <td key={col.key} className={`td-main ${col.className ?? ''}`.trim()}>
                {col.render(row, index)}
                <button
                  type="button"
                  className="table-card-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedKey(expanded ? null : key);
                  }}
                  aria-expanded={expanded}
                  aria-label="Perluas detail"
                >
                  <ChevronDown size={16} aria-hidden="true" />
                </button>
              </td>
            ) : (
              <td
                key={col.key}
                data-label={col.label}
                className={[col.align === 'right' ? 'num' : '', col.className]
                  .filter(Boolean)
                  .join(' ') || undefined}
              >
                {col.render(row, index)}
              </td>
            )
          )}
          {actions && (
            <td className={actionsClass} data-label={actionsLabel} onClick={(e) => e.stopPropagation()}>
              {actions(row)}
            </td>
          )}
        </tr>
      );
    });
  }

  const table = (
    <>
      <div className="table-container table-responsive">
        <table className={`table ${module}-table ${className}`.trim()}>
          <thead>
            <tr>
              {columns.map((col) =>
                col.sortKey && sort ? (
                  <SortableTh
                    key={col.key}
                    label={col.label}
                    sortKey={col.sortKey}
                    activeKey={sort.key}
                    dir={sort.dir}
                    onSort={sort.onSort}
                    align={col.align}
                    className={col.className}
                  />
                ) : (
                  <th
                    key={col.key}
                    className={[col.align === 'right' ? 'num' : '', col.className]
                      .filter(Boolean)
                      .join(' ') || undefined}
                  >
                    {col.header ?? col.label}
                  </th>
                )
              )}
              {actions && <th className={actionsClass}>{actionsLabel}</th>}
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      {pagination && pagination.total > 0 && !loading && !error && (
        <Pagination
          page={pagination.page}
          pageCount={pagination.pageCount}
          onChange={pagination.onChange}
          range={`Menampilkan ${pagination.from}–${pagination.to} dari ${pagination.total}`}
        />
      )}
    </>
  );

  return bare ? table : <Card>{table}</Card>;
}
